import { Section, SectionParagraph, SectionTitle } from '@/components/shared/Content';
import { useSectionEventOnViewportEntrance } from '@/hooks/useSectionEventOnViewportEntrance';
import { Flex, LegacyText } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import parse from 'html-react-parser';
import Image, { StaticImageData } from 'next/image';
import React, { useContext } from 'react';
import styled from 'styled-components';
import { CarPageContext } from '@/components/car/CarPageContext';

const StyledSection = styled(Section)`
  background-color: #ffffff;
`;

const StepsContainer = styled(Flex)`
  margin-top: 32px;
  gap: 24px;
  justify-content: center;
  flex-wrap: wrap;
  max-width: 1100px;

  ${MEDIA_QUERIES.mobile`
    flex-direction: column;
    align-items: center;
    gap: 14px;
  `}
`;

const StepCard = styled.div`
  background: #ffffff;
  border-radius: 12px;
  padding: 0 0 24px 0;
  width: 335px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  ${MEDIA_QUERIES.mobile`
    width: 100%;
    max-width: 100%;
    padding: 0;
  `}
`;

const IllustrationContainer = styled.div`
  width: 100%;
  height: 188px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  ${MEDIA_QUERIES.mobile`
    margin-bottom: 0px;
  `}
`;

const StepContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  white-space: pre-line;
`;

const StepTitle = styled(LegacyText).attrs({
  fontSize: '2rem',
  fontWeight: 700,
  lineHeight: ['2.2rem', '2.4rem'],
  type: 'main',
})`
  margin-bottom: 12px;
`;

const StepDescription = styled(LegacyText).attrs({
  fontSize: ['1.4rem', '1.6rem'],
  lineHeight: ['2rem', '2.4rem'],
  type: 'main',
})`
  color: #6b6b6b;
`;

export interface FSDHowItWorksStep {
  image: StaticImageData;
  title: string;
  description: string;
}

export interface FSDHowItWorksProps {
  title: string;
  subtitle?: string;
  steps?: FSDHowItWorksStep[];
}

const FSDHowItWorks: React.FC<FSDHowItWorksProps> = ({ title, subtitle, steps }) => {
  const { analytics } = useContext(CarPageContext);
  const intersectionRef = useSectionEventOnViewportEntrance({
    sectionName: 'how_it_works',
    page: analytics?.page,
  });

  return (
    <StyledSection mt={[30, 30, 50]} pt={[25, 36]} pb={[60, 60]} px={[16, 20]} ref={intersectionRef}>
      <Flex flexDirection="column" alignItems="center" maxWidth="800px" gap="24px">
        <SectionTitle tabIndex={0}>{parse(title)}</SectionTitle>
        {subtitle && <SectionParagraph tabIndex={0}>{parse(subtitle)}</SectionParagraph>}
      </Flex>

      {steps && steps.length > 0 && (
        <StepsContainer>
          {steps.map((step, index) => (
            <StepCard key={index}>
              <IllustrationContainer>
                <Image src={step.image} alt={step.title} fill fetchPriority="high" style={{ objectFit: 'contain' }} />
              </IllustrationContainer>
              <StepContent>
                <StepTitle>{step.title}</StepTitle>
                <StepDescription>{step.description}</StepDescription>
              </StepContent>
            </StepCard>
          ))}
        </StepsContainer>
      )}
    </StyledSection>
  );
};

export default React.memo(FSDHowItWorks);
